import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import ChipInput from "./ChipInput";
import { useToast } from "./Toast";

interface Props {
  body: string;
  onBodyChange: (s: string) => void;
  requestId: string;
  vars: Record<string, string>;
  envVarNames: string[];
  isResolved: (name: string) => boolean;
  resolveValue?: (name: string) => string | undefined;
}

// Persistent shape of the body field — exactly what the server expects
// to parse on the runtime side (see parseMultipartEnvelope).
interface TextField { kind: "text"; key: string; value: string }
interface FileField { kind: "file"; key: string; file_id: string; filename: string; size: number; content_type: string | null }
type Field = TextField | FileField;
interface Envelope { fields: Field[] }

function parseBody(body: string): Envelope {
  const trimmed = body.trim();
  if (!trimmed) return { fields: [] };
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed) as Envelope;
      if (Array.isArray(parsed.fields)) return parsed;
    } catch { /* fall through to legacy */ }
  }
  // Legacy `a=1&b=2` / `a=1\nb=2` text — silently convert.
  const out: Field[] = [];
  for (const raw of trimmed.split(/[&\n]/).map((s) => s.trim()).filter(Boolean)) {
    const eq = raw.indexOf("=");
    if (eq < 0) continue;
    out.push({ kind: "text", key: decodeURIComponent(raw.slice(0, eq)), value: decodeURIComponent(raw.slice(eq + 1)) });
  }
  return { fields: out };
}

function serialize(env: Envelope): string {
  // Drop completely empty rows (both key and value blank for text;
  // missing file_id for file) so we don't emit junk into the body.
  const fields = env.fields.filter((f) => {
    if (f.kind === "text") return f.key || f.value;
    return !!f.file_id;
  });
  return JSON.stringify({ fields });
}

const MAX_UPLOAD = 25 * 1024 * 1024;

export default function MultipartBodyEditor({ body, onBodyChange, requestId, vars, envVarNames, isResolved, resolveValue }: Props) {
  // Treat the parent's `body` string as authoritative; parse it for
  // the editor each render and serialize back on every change.
  const env = useMemo(() => parseBody(body), [body]);
  const fields = env.fields;
  const qc = useQueryClient();
  const toast = useToast();
  const fileInputRefs = useRef<Map<number, HTMLInputElement | null>>(new Map());
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);

  // Live list of files actually persisted server-side. We use this to
  // reconcile the envelope on load — if the user deleted a file in
  // another tab or session, the row needs to surface as "missing".
  const filesQ = useQuery({
    queryKey: ["request-files", requestId],
    queryFn: () => api.requestFiles.list(requestId),
    enabled: !!requestId,
  });
  const knownFileIds = useMemo(() => new Set((filesQ.data?.files ?? []).map((f) => f.id)), [filesQ.data]);

  // Always render at least one empty row so the user has somewhere to type.
  const slots: Field[] = fields.length === 0 ? [{ kind: "text", key: "", value: "" }] : fields;

  const update = (next: Field[]) => onBodyChange(serialize({ fields: next }));
  const setField = (idx: number, patch: Partial<Field>) => {
    const copy = slots.slice();
    copy[idx] = { ...copy[idx], ...patch } as Field;
    update(copy);
  };
  const removeField = (idx: number) => {
    const copy = slots.slice();
    copy.splice(idx, 1);
    update(copy);
  };
  const addText = () => update([...slots, { kind: "text", key: "", value: "" }]);
  const addFileSlot = () => update([...slots, { kind: "file", key: "", file_id: "", filename: "", size: 0, content_type: null }]);

  const upload = useMutation({
    mutationFn: async ({ idx, file }: { idx: number; file: File }) => {
      const key = slots[idx]?.key ?? "";
      const res = await api.requestFiles.upload(requestId, key || file.name, file);
      return { idx, res };
    },
    onMutate: ({ idx }) => setUploadingIdx(idx),
    onSuccess: ({ idx, res }) => {
      qc.invalidateQueries({ queryKey: ["request-files", requestId] });
      const copy = slots.slice();
      copy[idx] = {
        kind: "file",
        key: copy[idx]?.key || res.field_name,
        file_id: res.file_id,
        filename: res.filename,
        size: res.size,
        content_type: res.content_type,
      };
      update(copy);
      toast.success(`Uploaded ${res.filename}`);
    },
    onError: (e: unknown) => toast.error(`Upload failed — ${(e as Error).message}`),
    onSettled: () => setUploadingIdx(null),
  });

  const removeRemote = useMutation({
    mutationFn: (fileId: string) => api.requestFiles.remove(requestId, fileId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["request-files", requestId] }),
  });

  return (
    <div className="space-y-2 min-w-0">
      <div className="text-xs muted">
        Multipart form. Mix text fields and file uploads. Files (up to 25&nbsp;MB each) live in your workspace storage and are streamed into the request at send time.
      </div>
      <ul className="space-y-1.5">
        {slots.map((f, idx) => {
          if (f.kind === "text") {
            return (
              <li key={idx} className="grid grid-cols-[1fr_1.5fr_auto] gap-2 items-center">
                <KeyInput value={f.key} onChange={(v) => setField(idx, { key: v })} suggestions={envVarNames} vars={vars} isResolved={isResolved} resolveValue={resolveValue} placeholder="field" />
                <KeyInput value={f.value} onChange={(v) => setField(idx, { value: v } as Partial<TextField>)} suggestions={envVarNames} vars={vars} isResolved={isResolved} resolveValue={resolveValue} placeholder="value" />
                <RowActions onRemove={() => removeField(idx)} />
              </li>
            );
          }
          const missing = !!f.file_id && !knownFileIds.has(f.file_id);
          return (
            <li key={idx} className="grid grid-cols-[1fr_1.5fr_auto] gap-2 items-center">
              <KeyInput value={f.key} onChange={(v) => setField(idx, { key: v } as Partial<FileField>)} suggestions={envVarNames} vars={vars} isResolved={isResolved} resolveValue={resolveValue} placeholder="field name" />
              <div className="flex items-center gap-2 min-w-0">
                {f.file_id ? (
                  <div className={"flex-1 min-w-0 truncate text-xs rounded border px-2 py-[6px] " + (missing ? "border-danger/60 text-danger" : "border-border")}>
                    📎 {f.filename || "file"} <span className="muted">({formatBytes(f.size)}{f.content_type ? ` · ${f.content_type}` : ""})</span>
                    {missing && <span className="ml-1">— missing on server</span>}
                  </div>
                ) : (
                  <span className="flex-1 muted text-xs">No file yet</span>
                )}
                <input
                  ref={(el) => { fileInputRefs.current.set(idx, el); }}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > MAX_UPLOAD) {
                      toast.error(`File too large — max ${formatBytes(MAX_UPLOAD)}`);
                      e.target.value = "";
                      return;
                    }
                    upload.mutate({ idx, file });
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  className="btn text-xs"
                  disabled={uploadingIdx === idx}
                  onClick={() => fileInputRefs.current.get(idx)?.click()}
                >{uploadingIdx === idx ? "Uploading…" : f.file_id ? "Replace" : "Choose…"}</button>
                {f.file_id && !missing && (
                  <button
                    type="button"
                    className="btn text-xs"
                    disabled={removeRemote.isPending}
                    onClick={() => { removeRemote.mutate(f.file_id); setField(idx, { file_id: "", filename: "", size: 0, content_type: null } as Partial<FileField>); }}
                  >Clear</button>
                )}
              </div>
              <RowActions onRemove={() => {
                if (f.file_id) removeRemote.mutate(f.file_id);
                removeField(idx);
              }} />
            </li>
          );
        })}
      </ul>
      <div className="flex items-center gap-2">
        <button type="button" className="btn text-xs" onClick={addText}>+ Text field</button>
        <button type="button" className="btn text-xs" onClick={addFileSlot}>+ File field</button>
      </div>
    </div>
  );
}

function KeyInput({
  value, onChange, suggestions, vars, isResolved, resolveValue, placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  suggestions: string[];
  vars: Record<string, string>;
  isResolved: (name: string) => boolean;
  resolveValue?: (name: string) => string | undefined;
  placeholder?: string;
}) {
  // Reuse the ChipInput everywhere else uses for env-var chips — keeps
  // {{HOST}} inside multipart fields rendering identically to the URL bar.
  return (
    <ChipInput
      kind="var"
      value={value}
      onChange={onChange}
      suggestions={suggestions.map((n) => ({ name: n, hint: vars[n] ?? "" }))}
      isResolved={isResolved}
      resolveValue={resolveValue}
      placeholder={placeholder}
    />
  );
}

function RowActions({ onRemove }: { onRemove: () => void }) {
  return (
    <button
      type="button"
      onClick={onRemove}
      className="h-7 w-7 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-3"
      title="Remove"
      aria-label="Remove field"
    >×</button>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

